import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../Redux/Hooks";
import {
  getQuotationDashboardStatsAPI,
  getAllQuotationAPI,
} from "../../../Redux/ApiCalls/Dashboard/QuotationAPI";
import { getOrderTransactionsAPI } from "../../../Redux/ApiCalls/Dashboard/OrderAPI";
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  Camera,
  Eye,
  Sparkles,
  FileText,
  IndianRupee,
  Wallet,
  Receipt,
  ArrowUpRight,
} from "lucide-react";
import { device } from "../../atoms/Utlis/media";

// Theme
const GRADIENT = "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";
const PURPLE = "#c94daa";
const CHART_ORANGE = "#F2AD6D";
const CHART_PURPLE = "#D776A4";
const CHART_GRADIENT = "linear-gradient(180.14deg, #F2AD6D 0.11%, #D776A4 99.87%)";
const SOFT_PURPLE = "rgba(215,118,164,0.16)";

// Styled Components

const Page = styled.div`
  width: 100%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-sizing: border-box;
  font-family: "Urbanist", sans-serif;
  flex-shrink: 0;
  @media ${device.tablet} { padding: 16px; }
`;

const Hero = styled.div`
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  border-radius: 20px;
  padding: 24px 26px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.16) 0%,
    rgba(206, 86, 176, 0.12) 55%,
    rgba(53, 183, 200, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  box-shadow: 0 8px 28px rgba(201, 77, 170, 0.08);
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -80px;
  right: -4%;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.32) 0%, transparent 70%);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.25;
  padding: 2px 0;
`;

const PageSubtitle = styled.p`
  margin: 0px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 560px;
`;

const ContentWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  flex-shrink: 0;
  @media ${device.laptop} {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const WalletHero = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  color: #fff;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%);
  box-shadow: 0 8px 22px rgba(201, 77, 170, 0.22);
  text-align: left;
  &::after {
    content: "";
    position: absolute;
    right: -20px;
    bottom: -24px;
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const WalletIconWrap = styled.div`
  position: relative;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.22);
`;

const WalletHeroBody = styled.div`
  position: relative;
  z-index: 1;
  min-width: 0;
`;

const WalletHeroValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  line-height: 1.1;
`;

const WalletHeroLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  margin-top: 2px;
`;

const StatCard = styled.div<{ $tone: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 6px 18px rgba(23, 23, 23, 0.04);
`;

const StatIcon = styled.div<{ $tone: string }>`
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => p.$tone};
  background: ${(p) => `${p.$tone}18`};
`;

const StatBody = styled.div`
  min-width: 0;
`;

const StatValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
  line-height: 1.1;
`;

const StatLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
  margin-top: 2px;
`;

const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  gap: 12px;
  color: rgba(23, 23, 23, 0.5);
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 600;
`;

// Middle row: two panels side by side
const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const Panel = styled.div`
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 10px 32px rgba(23, 23, 23, 0.06);
  border: 1px solid rgba(23, 23, 23, 0.06);
  overflow: hidden;
`;

const PanelHeader = styled.div`
  padding: 18px 22px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-family: "Urbanist", sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
`;

// Semi-donut gauge
const GaugeWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 20px 0;
  position: relative;
`;

const GaugeCenterText = styled.div`
  text-align: center;
  margin-top: -32px;
  margin-bottom: 12px;
`;

const GaugeTotalLabel = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: rgba(23,23,23,0.45);
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const GaugeTotalValue = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 21px;
  font-weight: 700;
  color: rgba(23,23,23,0.86);
`;

const ServiceLegendRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px 16px;
  border-top: 1px solid rgba(23,23,23,0.06);
`;

const ServiceLegendLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  color: #171717;
`;

const LegendDot = styled.span<{ $color?: string; $gradient?: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${p => (p.$gradient ? CHART_GRADIENT : p.$color)};
  display: inline-block;
  flex-shrink: 0;
`;

const ServiceLegendAmount = styled.span`
  font-family: "Urbanist", sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #171717;
`;

// Overtime chart
const ChartWrap = styled.div`
  padding: 10px 16px 16px;
  height: 240px;
`;

const OvertimeLegend = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 20px 8px;
  justify-content: flex-end;
`;

const OvertimeLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: "Montserrat", sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23,23,23,0.65);
`;

const OvertimeDot = styled.span<{ $color?: string; $gradient?: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${p => (p.$gradient ? CHART_GRADIENT : p.$color)};
  display: inline-block;
`;

// Top 5 Resources
const ResourceList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0 8px;
`;

const ResourceItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 22px;
  border-bottom: 1px solid rgba(23,23,23,0.05);
  transition: background 0.15s ease;
  &:last-child { border-bottom: none; }
  &:hover { background: rgba(201, 77, 170, 0.03); }
`;

const ResourceIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: ${GRADIENT};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 6px 16px rgba(201, 77, 170, 0.2);
`;

const ResourceInfo = styled.div`
  flex: 1;
`;

const ResourceName = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #171717;
`;

const ResourceUses = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 11px;
  color: rgba(23,23,23,0.45);
  margin-top: 2px;
`;

const ResourceRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
`;

const TopBadge = styled.span`
  font-family: "Urbanist", sans-serif;
  font-size: 10px;
  font-weight: 700;
  color: ${PURPLE};
  background: rgba(201,77,170,0.1);
  border-radius: 999px;
  padding: 2px 8px;
`;

const ResourceAmount = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23,23,23,0.5);
`;

// Income vs Expense
const IncomeExpenseWrap = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 20px 20px;
  gap: 12px;
`;

const IncomeExpenseHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ViewAllBtn = styled.button`
  padding: 6px 14px;
  border: 1px solid rgba(23,23,23,0.1);
  border-radius: 10px;
  background: #fff;
  font-family: "Urbanist", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(23,23,23,0.65);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
`;

const NoDataBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 90px;
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  color: rgba(23,23,23,0.38);
  background: rgba(23,23,23,0.02);
  border-radius: 14px;
  border: 1px dashed rgba(23, 23, 23, 0.08);
`;

const IncomeTotalsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border: 1px solid rgba(23,23,23,0.08);
  border-radius: 14px;
  overflow: hidden;
`;

const IncomeTile = styled.div<{ $tone?: string }>`
  padding: 16px 20px;
  border-right: 1px solid rgba(23,23,23,0.08);
  background: ${(p) => (p.$tone ? `${p.$tone}08` : "#fff")};
  &:last-child { border-right: none; }
`;

const IncomeTileValue = styled.div<{ $tone?: string }>`
  font-family: "Urbanist", sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: ${(p) => p.$tone || "rgba(23,23,23,0.86)"};
  letter-spacing: -0.02em;
`;

const IncomeTileLabel = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 11px;
  color: rgba(23,23,23,0.45);
  margin-top: 4px;
`;

// Latest Order table (full width)
const TableWrap = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  min-width: 800px;
  border-collapse: collapse;
  font-family: "Montserrat", sans-serif;

  thead tr {
    background: transparent;
  }
  th {
    padding: 13px 18px;
    text-align: left;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    white-space: nowrap;
  }
  th span {
    background: linear-gradient(144.01deg, #FFC951 0%, #C94DAA 80%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
    display: inline-block;
  }
  td {
    padding: 13px 18px;
    font-size: 13px;
    font-weight: 500;
    color: rgba(23,23,23,0.82);
    border-bottom: 1px solid rgba(23,23,23,0.06);
  }
  tbody tr:last-child td { border-bottom: none; }
  tbody tr:hover { background: rgba(201,77,170,0.04); }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  background: rgba(34,197,94,0.12);
  color: #16a34a;
  border: 1px solid rgba(34,197,94,0.3);
`;

const EyeBtn = styled.button`
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 10px;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.15) 0%, rgba(206, 86, 176, 0.15) 100%);
  color: #c94daa;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%);
    color: #fff;
    transform: translateY(-1px);
  }
`;

const CustomerCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CustomerName = styled.div`
  font-weight: 600;
  color: #171717;
`;

const CustomerMobile = styled.div`
  font-size: 11px;
  color: rgba(23,23,23,0.45);
`;

// Tooltip
const TooltipBox = styled.div`
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  padding: 8px 12px;
  font-family: "Urbanist", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(23,23,23,0.86);
`;

const formatINR = (v: number) =>
  "₹" + new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(v);

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <TooltipBox>
      <div style={{ color: "#9ca3af", marginBottom: 4, fontSize: 11 }}>{label}</div>
      {payload.map((p: any, i: number) => (
        <div key={i} style={{ color: p.color }}>{p.name}: {formatINR(p.value)}</div>
      ))}
    </TooltipBox>
  );
};

// Component
const QuotationDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { allQuotations } = useAppSelector((state) => state.quotation);
  const { selectedBranch } = useAppSelector((state) => state.auth);

  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<any[]>([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await getQuotationDashboardStatsAPI({ branch_id: selectedBranch?.id });
        setStats(data);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };

    const fetchTx = async () => {
      try {
        const res = await getOrderTransactionsAPI({ page_size: 1000, branch_id: selectedBranch?.id });
        const results = res?.results || res || [];
        setTransactions(results);
      } catch (e) { }
    };

    fetchStats();
    fetchTx();
    dispatch(getAllQuotationAPI({ page_size: 5, branch_id: selectedBranch?.id }));
  }, [dispatch, selectedBranch?.id]);

  const { totalIncome, totalExpense } = useMemo(() => {
    let income = 0;
    let expense = 0;
    transactions.forEach((t) => {
      if (t.type === "Income") {
        income += Number(t.credit_amount || 0);
      } else if (t.type === "Expense") {
        expense += Number(t.debit_amount || 0);
      }
    });
    return { totalIncome: income, totalExpense: expense };
  }, [transactions]);

  const kpiData = useMemo(() => {
    if (!stats || !stats.summary) {
      return {
        total: "₹0.00",
        received: "₹0.00",
        due: "₹0.00",
        expense: `₹${totalExpense.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
      };
    }
    const revs = stats.summary.revenue_by_status || {};
    const totalAmount = Object.values(revs).reduce((acc: number, val: any) => acc + Number(val || 0), 0);
    const paymentReceived = Number(revs.accepted || 0);
    const duePayment = totalAmount - paymentReceived;

    return {
      total: `₹${totalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
      received: `₹${paymentReceived.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
      due: `₹${duePayment.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
      expense: `₹${totalExpense.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
    };
  }, [stats, totalExpense]);

  const totalRevenue = useMemo(() => {
    if (!stats || !stats.summary) return 0;
    const revs = stats.summary.revenue_by_status || {};
    return Object.values(revs).reduce((acc: number, val: any) => acc + Number(val || 0), 0);
  }, [stats]);

  const paymentReceived = useMemo(() => {
    if (!stats || !stats.summary) return 0;
    const revs = stats.summary.revenue_by_status || {};
    return Number(revs.accepted || 0);
  }, [stats]);

  const duePayment = useMemo(() => {
    return totalRevenue - paymentReceived;
  }, [totalRevenue, paymentReceived]);

  const serviceCategories = useMemo(() => {
    return [
      { name: "Quotation Payment", value: paymentReceived, color: CHART_PURPLE },
      { name: "Remaining", value: duePayment, color: SOFT_PURPLE },
    ];
  }, [paymentReceived, duePayment]);

  const overtimeData = useMemo(() => {
    if (!stats || !stats.trends) return [];
    const monthNames = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return stats.trends.map((t: any) => ({
      date: monthNames[t.month] || `Month ${t.month}`,
      quotation: Number(t.total_amount) || 0,
    }));
  }, [stats]);

  const top5Resources = useMemo(() => {
    if (!stats || !stats.top_resources) return [];
    return stats.top_resources.map((r: any) => ({
      name: r.name,
      uses: r.usage_count,
      amount: `₹${Number(r.allocated_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
    }));
  }, [stats]);

  if (loading) {
    return (
      <Page>
        <LoadingWrap>
          <Sparkles size={28} color="#c94daa" />
          Loading dashboard...
        </LoadingWrap>
      </Page>
    );
  }

  return (
    <Page>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <div className="flex flex-col">
            <HeroBadge>
              <Sparkles size={13} />
              Quotation insights
            </HeroBadge>
            <PageTitle className="linear-text">Dashboard</PageTitle>
            <PageSubtitle>
              Track quotation revenue, payments, resources, and financial performance at a glance.
            </PageSubtitle>
          </div>
        </HeroContent>
      </Hero>

      <ContentWrap>
        <StatsGrid>
          <WalletHero>
            <WalletIconWrap>
              <FileText size={16} />
            </WalletIconWrap>
            <WalletHeroBody>
              <WalletHeroValue>{kpiData.total}</WalletHeroValue>
              <WalletHeroLabel>Quotation amount</WalletHeroLabel>
            </WalletHeroBody>
          </WalletHero>

          <StatCard $tone="#31a66a">
            <StatIcon $tone="#31a66a">
              <Wallet size={16} />
            </StatIcon>
            <StatBody>
              <StatValue>{kpiData.received}</StatValue>
              <StatLabel>Payment received</StatLabel>
            </StatBody>
          </StatCard>

          <StatCard $tone="#e44862">
            <StatIcon $tone="#e44862">
              <IndianRupee size={16} />
            </StatIcon>
            <StatBody>
              <StatValue>{kpiData.due}</StatValue>
              <StatLabel>Due payment</StatLabel>
            </StatBody>
          </StatCard>

          <StatCard $tone="#35b7c8">
            <StatIcon $tone="#35b7c8">
              <Receipt size={16} />
            </StatIcon>
            <StatBody>
              <StatValue>{kpiData.expense}</StatValue>
              <StatLabel>Quotation expense</StatLabel>
            </StatBody>
          </StatCard>
        </StatsGrid>

        {/* Row 2: Top Service Categories + Quotation vs Order Overtime */}
        <TwoCol>
          {/* Left: Semi-Donut Gauge */}
          <Panel>
            <PanelHeader>
              <PanelTitle>Top Service Categories</PanelTitle>
            </PanelHeader>
            <GaugeWrap>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <defs>
                    <linearGradient id="servicePaymentGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0.11%" stopColor={CHART_ORANGE} />
                      <stop offset="99.87%" stopColor={CHART_PURPLE} />
                    </linearGradient>
                    <linearGradient id="serviceRemainingGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0.11%" stopColor={CHART_ORANGE} stopOpacity={0.18} />
                      <stop offset="99.87%" stopColor={CHART_PURPLE} stopOpacity={0.18} />
                    </linearGradient>
                  </defs>
                  <Pie
                    data={serviceCategories}
                    cx="50%"
                    cy="100%"
                    startAngle={180}
                    endAngle={0}
                    innerRadius={80}
                    outerRadius={130}
                    paddingAngle={2}
                    dataKey="value"
                    stroke="none"
                  >
                    {serviceCategories.map((entry, i) => (
                      <Cell key={i} fill={i === 0 ? "url(#servicePaymentGrad)" : "url(#serviceRemainingGrad)"} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <GaugeCenterText>
                <GaugeTotalLabel>TOTAL BALANCE</GaugeTotalLabel>
                <GaugeTotalValue>₹{totalRevenue.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</GaugeTotalValue>
              </GaugeCenterText>
            </GaugeWrap>
            <ServiceLegendRow>
              <ServiceLegendLeft>
                <LegendDot $gradient />
                Quotation Payment
              </ServiceLegendLeft>
              <ServiceLegendAmount>₹{paymentReceived.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</ServiceLegendAmount>
            </ServiceLegendRow>
          </Panel>

          {/* Right: Quotation vs Order Overtime */}
          <Panel>
            <PanelHeader>
              <PanelTitle>Quotation Overtime</PanelTitle>
            </PanelHeader>
            <OvertimeLegend>
              <OvertimeLegendItem>
                <OvertimeDot $gradient />
                Quotation Amount
              </OvertimeLegendItem>
            </OvertimeLegend>
            <ChartWrap>
              <ResponsiveContainer width="100%" height="100%">
                {overtimeData.length > 0 ? (
                  <AreaChart data={overtimeData} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="quotationGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0.11%" stopColor={CHART_ORANGE} stopOpacity={0.24} />
                        <stop offset="99.87%" stopColor={CHART_PURPLE} stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="themeLineGrad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0.11%" stopColor={CHART_ORANGE} />
                        <stop offset="99.87%" stopColor={CHART_PURPLE} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="rgba(0,0,0,0.04)" strokeDasharray="4 4" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: "#9ca3af", fontFamily: "Montserrat" }}
                      axisLine={{ stroke: "rgba(0,0,0,0.1)" }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#9ca3af", fontFamily: "Montserrat" }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`}
                      width={52}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="quotation"
                      name="Quotation"
                      stroke="url(#themeLineGrad)"
                      strokeWidth={2}
                      fill="url(#quotationGrad)"
                      dot={false}
                    />
                  </AreaChart>
                ) : (
                  <NoDataBox style={{ height: "100%" }}>No trend data available.</NoDataBox>
                )}
              </ResponsiveContainer>
            </ChartWrap>
          </Panel>
        </TwoCol>

        {/* Row 3: Top 5 Resources + Income vs Expense */}
        <TwoCol>
          {/* Left: Top 5 Resources */}
          <Panel>
            <PanelHeader>
              <PanelTitle>Top 5 Resources, Count and Amount</PanelTitle>
            </PanelHeader>
            <ResourceList>
              {top5Resources.map((r: any, i: number) => (
                <ResourceItem key={i}>
                  <ResourceIcon>
                    <Camera size={16} color="#fff" />
                  </ResourceIcon>
                  <ResourceInfo>
                    <ResourceName>{r.name}</ResourceName>
                    <ResourceUses>{r.uses} Uses</ResourceUses>
                  </ResourceInfo>
                  <ResourceRight>
                    <TopBadge>↑ Top</TopBadge>
                    <ResourceAmount>{r.amount}</ResourceAmount>
                  </ResourceRight>
                </ResourceItem>
              ))}
              {top5Resources.length === 0 && (
                <NoDataBox style={{ margin: 20 }}>No resource usage data available.</NoDataBox>
              )}
            </ResourceList>
          </Panel>

          {/* Right: Income vs Expense Statistics */}
          <Panel>
            <PanelHeader>
              <PanelTitle>Income Vs Expense Statistics</PanelTitle>
              <ViewAllBtn>
                View All ▼
              </ViewAllBtn>
            </PanelHeader>
            <IncomeExpenseWrap>
              {transactions.length === 0 && <NoDataBox>No income/expense data available.</NoDataBox>}
              <IncomeTotalsRow>
                <IncomeTile $tone="#31a66a">
                  <IncomeTileValue $tone="#31a66a">₹{totalIncome.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</IncomeTileValue>
                  <IncomeTileLabel>Total Income</IncomeTileLabel>
                </IncomeTile>
                <IncomeTile $tone="#e44862">
                  <IncomeTileValue $tone="#e44862">₹{totalExpense.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</IncomeTileValue>
                  <IncomeTileLabel>Total Expense</IncomeTileLabel>
                </IncomeTile>
              </IncomeTotalsRow>
            </IncomeExpenseWrap>
          </Panel>
        </TwoCol>

        {/* Row 4: Latest Quotations (full width) */}
        <Panel>
          <PanelHeader style={{ paddingBottom: 12 }}>
            <PanelTitle>Latest Quotations</PanelTitle>
            <ViewAllBtn onClick={() => navigate("/dashboard/quotation")}>
              View all <ArrowUpRight size={14} />
            </ViewAllBtn>
          </PanelHeader>
          <TableWrap>
            <StyledTable>
              <thead>
                <tr>
                  <th><span>Quotation No</span></th>
                  <th><span>Date</span></th>
                  <th><span>Customer</span></th>
                  <th><span>Amount</span></th>
                  <th><span>Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {allQuotations.slice(0, 5).map((o, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 700, color: "#171717" }}>{o.quotation_no}</td>
                    <td>{o.quotation_date}</td>
                    <td>
                      <CustomerCell>
                        <CustomerName>{o.customer_name}</CustomerName>
                        <CustomerMobile>{o.customer_phone}</CustomerMobile>
                      </CustomerCell>
                    </td>
                    <td style={{ fontWeight: 700, color: PURPLE }}>{o.without_price ? "-" : `₹${Number(o.grand_total).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}</td>
                    <td>
                      <EyeBtn aria-label="View order" onClick={() => navigate(`/dashboard/quotation/view?id=${o.id}`)}>
                        <Eye size={15} />
                      </EyeBtn>
                    </td>
                  </tr>
                ))}
                {allQuotations.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center", padding: 24, color: "rgba(23,23,23,0.4)" }}>
                      No quotations found.
                    </td>
                  </tr>
                )}
              </tbody>
            </StyledTable>
          </TableWrap>
        </Panel>
      </ContentWrap>
    </Page>
  );
};

export default QuotationDashboardPage;
